<template>
    <el-dialog
        class="ve-column-config-dialog"
        :visible="visible"
        width="340px"
        :close-on-press-escape="false"
        :close-on-click-modal="false"
        append-to-body
        top="4vh"
        v-dialogDrag
        @open="handleOpen"
        @close="handleClose"
    >
        <div slot="title" class="ve-column-config-title" :style="{'background':'#409eff','color':'white','height':'40px'}">
            <div class="ve-column-config-title-main">{{ dialogLabels.title }}</div>
        </div>
        <div v-loading="loading" class="ve-column-config-body">
            <div class="ve-column-config-table">
                <div class="ve-column-config-table-header">
                    <div class="ve-column-config-check-cell">
                        <el-checkbox
                            v-model="allSelectedModel"
                            :disabled="loading"
                            class="ve-column-config-check-all"
                        >{{ dialogLabels.all }}</el-checkbox>
                    </div>
                    <div class="ve-column-config-title-cell">{{ dialogLabels.columnTitle }}</div>
                </div>
                <div ref="fieldList" class="ve-column-config-table-body">
                    <div
                        v-for="item in draftRows"
                        :key="item.CODE"
                        :data-field-key="item.CODE"
                        class="ve-column-config-row"
                    >
                        <div class="ve-column-config-check-cell">
                            <el-checkbox
                                v-model="item.__fieldSettingSelected"
                                :disabled="loading || isLockedKey(item.CODE)"
                                @change="checked => toggleField(item, checked)"
                            ></el-checkbox>
                        </div>
                        <div class="ve-column-config-title-cell">
                            <span
                                :class="['ve-column-config-drag', { 'is-locked': isLockedKey(item.CODE) }]"
                            ><i class="el-icon-d-caret drag-btn"></i></span>
                            <span class="ve-column-config-name">{{ item.DISPNM || item.CNAME }}</span>
                        </div>
                    </div>
                </div>
            </div>
            <div v-if="errorText" class="ve-column-config-error">{{ errorText }}</div>
        </div>
            <div slot="footer" class="ve-column-config-footer">
                <el-button size="small" :disabled="loading || isRefreshError" @click="restoreDefault">{{ dialogLabels.restore }}</el-button>
                <span class="ve-column-config-footer-right">
                    <el-button size="small" type="primary" :disabled="isSaveDisabled" @click="saveConfig">{{ dialogLabels.save }}</el-button>
            </span>
        </div>
    </el-dialog>
</template>

<script>
import Sortable from 'sortablejs'
import { getFieldKey } from '@/utils/formFieldSettingsService'

function cloneRows(rows) {
    return (rows || []).map(row => Object.assign({}, row))
}

const DEFAULT_DIALOG_LABELS = {
    CN: {
        title: '列设置',
        all: '全部',
        columnTitle: '列标题',
        restore: '恢复默认',
        save: '保存并应用',
    },
    EN: {
        title: 'Column settings',
        all: 'All',
        columnTitle: 'Column title',
        restore: 'Restore defaults',
        save: 'Save and apply',
    },
    YN: {
        title: 'Cài đặt cột',
        all: 'Tất cả',
        columnTitle: 'Tiêu đề cột',
        restore: 'Khôi phục mặc định',
        save: 'Lưu và áp dụng',
    },
}

export default {
    name: 'VeColumnConfigDialog',
    props: {
        visible: {
            type: Boolean,
            default: false,
        },
        rows: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        error: {
            type: [String, Object],
            default: '',
        },
        lockedFieldKeys: {
            type: Array,
            default: () => [],
        },
        dragGroupField: {
            type: String,
            default: '',
        },
        language: {
            type: String,
            default: 'CN',
        },
        labels: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            draftRows: [],
            sortable: null,
        }
    },
    computed: {
        dialogLabels() {
            const defaults = DEFAULT_DIALOG_LABELS[this.language] || DEFAULT_DIALOG_LABELS.CN
            return Object.assign({}, defaults, this.labels)
        },
        isAllSelected() {
            return this.draftRows.length > 0 && this.draftRows.every(item => this.isSelected(item.CODE))
        },
        allSelectedModel: {
            get() {
                return this.isAllSelected
            },
            set(checked) {
                this.toggleAllVisibleFields(checked)
            },
        },
        isSaveDisabled() {
            return this.loading || this.isRefreshError || this.draftRows.length === 0 || (!!this.errorText && this.rows.length === 0)
        },
        errorText() {
            if (!this.error) return ''
            if (typeof this.error === 'object') return this.error.message || ''
            return this.error
        },
        isRefreshError() {
            return !!this.error && typeof this.error === 'object' && this.error.type === 'refresh'
        },
    },
    watch: {
        rows: {
            handler() {
                this.syncDraftRows()
                if (this.visible) this.$nextTick(this.initSortable)
            },
            deep: false,
        },
        lockedFieldKeys() {
            this.syncDraftRows()
        },
        visible(value) {
            if (!value) {
                this.destroySortable()
                return
            }
            this.syncDraftRows()
            this.$nextTick(this.initSortable)
        },
    },
    beforeDestroy() {
        this.destroySortable()
    },
    methods: {
        getFieldKey,
        isLockedKey(key) {
            const identity = String(key || '').trim().toUpperCase()
            return this.lockedFieldKeys.some(item => String(item || '').trim().toUpperCase() === identity)
        },
        isSelected(key) {
            const identity = String(key || '').trim().toUpperCase()
            const row = this.draftRows.find(item => String(getFieldKey(item) || '').trim().toUpperCase() === identity)
            return !!row && String(row.VISFLAG || row.visflag || '').trim().toUpperCase() !== 'N'
        },
        isSameDragGroup(leftKey, rightKey) {
            if (!this.dragGroupField) return true
            const leftIdentity = String(leftKey || '').trim().toUpperCase()
            const rightIdentity = String(rightKey || '').trim().toUpperCase()
            const left = this.draftRows.find(item => String(getFieldKey(item) || '').trim().toUpperCase() === leftIdentity)
            const right = rightKey
                ? this.draftRows.find(item => String(getFieldKey(item) || '').trim().toUpperCase() === rightIdentity)
                : this.draftRows[this.draftRows.length - 1]
            if (!left || !right) return true
            return !!left && !!right && left.__fieldSettingDragGroup === right.__fieldSettingDragGroup
        },
        normalizeDraftRows(rows) {
            return cloneRows(rows).map(row => {
                const locked = this.isLockedKey(getFieldKey(row))
                const visibleFlag = String(row.VISFLAG || row.visflag || '').trim().toUpperCase()
                return Object.assign(row, {
                    VISFLAG: locked || visibleFlag !== 'N' ? 'Y' : 'N',
                    __fieldSettingSelected: locked || visibleFlag !== 'N',
                })
            })
        },
        syncDraftRows() {
            this.draftRows = this.normalizeDraftRows(cloneRows(this.rows))
        },
        handleOpen() {
            this.syncDraftRows()
            this.$nextTick(this.initSortable)
        },
        handleClose() {
            this.destroySortable()
            this.draftRows = []
            this.$emit('update:visible', false)
            this.$emit('close')
        },
        emitChange() {
            this.$emit('change', cloneRows(this.draftRows))
        },
        toggleField(item, checked) {
            if (this.loading) return
            const key = getFieldKey(item)
            const identity = String(key || '').trim().toUpperCase()
            const row = this.draftRows.find(draftRow => String(getFieldKey(draftRow) || '').trim().toUpperCase() === identity)
            if (!row || (!checked && this.isLockedKey(key))) return
            this.$set(row, '__fieldSettingSelected', !!checked)
            this.$set(row, 'VISFLAG', checked ? 'Y' : 'N')
        },
        toggleAllVisibleFields(checked) {
            if (this.loading) return
            this.draftRows.forEach(row => {
                if (this.isLockedKey(getFieldKey(row))) {
                    this.$set(row, '__fieldSettingSelected', true)
                    this.$set(row, 'VISFLAG', 'Y')
                    return
                }
                this.$set(row, '__fieldSettingSelected', !!checked)
                this.$set(row, 'VISFLAG', checked ? 'Y' : 'N')
            })
        },
        restoreDefault() {
            if (this.loading) return
            this.$emit('reset')
        },
        initSortable() {
            this.destroySortable()
            const element = this.$refs.fieldList
            if (!element || !this.visible) return
            this.sortable = new Sortable(element, {
                filter: '.ve-column-config-check-cell',
                preventOnFilter: false,
                animation: 150,
                onMove: event => {
                    const draggedKey = event.dragged && event.dragged.dataset.fieldKey
                    const relatedKey = event.related && event.related.dataset.fieldKey
                    // Sortable can report no related row while the pointer is in the
                    // whitespace below the list. The final group check in onEnd
                    // uses the proposed array position, so allow that interim move.
                    return !this.loading && (!relatedKey || this.isSameDragGroup(draggedKey, relatedKey))
                },
                onEnd: ({ newIndex, oldIndex, item }) => {
                    const sourceIndex = Number(oldIndex)
                    const targetIndex = Number(newIndex)
                    if (!Number.isInteger(sourceIndex) || !Number.isInteger(targetIndex)) {
                        this.draftRows = this.normalizeDraftRows(cloneRows(this.draftRows))
                        this.$nextTick(this.initSortable)
                        return
                    }
                    if (targetIndex === sourceIndex) return
                    const dragRow = this.draftRows[sourceIndex]
                    if (!dragRow) {
                        this.draftRows = this.normalizeDraftRows(cloneRows(this.draftRows))
                        this.$nextTick(this.initSortable)
                        return
                    }
                    const draggedKey = item && item.dataset.fieldKey || getFieldKey(dragRow)
                    const proposedRows = this.draftRows.slice()
                    proposedRows.splice(sourceIndex, 1)
                    const proposedIndex = Math.max(0, Math.min(targetIndex, proposedRows.length))
                    // After removing the source row, a downward drop lands
                    // after the preceding row; an upward drop lands before
                    // the row at the target index.
                    const targetRow = targetIndex > sourceIndex
                        ? proposedRows[proposedIndex - 1] || proposedRows[proposedRows.length - 1]
                        : proposedRows[proposedIndex] || proposedRows[proposedIndex - 1]
                    const targetKey = targetRow && getFieldKey(targetRow)
                    if (this.dragGroupField && targetKey
                        && !this.isSameDragGroup(draggedKey, targetKey)) {
                        // Sortable has already moved the DOM node. Reassigning a
                        // cloned array makes Vue restore the rejected order before
                        // Sortable is initialized again.
                        this.draftRows = this.normalizeDraftRows(cloneRows(this.draftRows))
                        this.$nextTick(this.initSortable)
                        return
                    }
                    this.draftRows.splice(sourceIndex, 1)
                    this.draftRows.splice(proposedIndex, 0, dragRow)
                },
            })
        },
        destroySortable() {
            if (!this.sortable) return
            this.sortable.destroy()
            this.sortable = null
        },
        saveConfig() {
            if (this.isSaveDisabled) return
            this.emitChange()
            this.$emit('confirm', cloneRows(this.draftRows))
        },
    },
}
</script>

<style scoped lang="less">
.ve-column-config-dialog {
    /deep/ .el-dialog__header {
        padding:0;
        height:40px;
    }
    /deep/ .el-dialog__body {
        padding:0;
    }
    /deep/ .el-dialog__footer {
        padding:12px 40px;
        border-top:1px solid #e5e9f0;
    }
    /deep/ .el-dialog__headerbtn {
        top:10px;
        right:20px;
    }
}
.ve-column-config-title {
    height:40px;
    padding:0 20px;
    border-bottom:1px solid #e5e9f0;
    box-sizing:border-box;
}
.ve-column-config-title-main {
    font-size:16px;
    line-height:40px;
    font-weight:700;
    color:#fff;
}
.ve-column-config-body {
    display:block;
    height:640px;
    min-height:420px;
    padding:20px;
    box-sizing:border-box;
}
.ve-column-config-table {
    display:flex;
    flex-direction:column;
    height:100%;
    border:1px solid #dfe5ee;
    box-sizing:border-box;
    overflow:hidden;
}
.ve-column-config-table-header,
.ve-column-config-row {
    display:grid;
    grid-template-columns:70px 1fr;
}
.ve-column-config-table-header {
    height:40px;
    line-height:40px;
    background:#f8fafc;
    border-bottom:1px solid #e5e9f0;
    color:#2f343b;
    font-size:14px;
    font-weight:700;
}
.ve-column-config-table-body {
    flex:1;
    overflow:auto;
}
.ve-column-config-error {
    margin-top:10px;
    padding:8px 10px;
    color:#c0362c;
    background:#fff2f0;
    border:1px solid #f3c4bf;
    font-size:12px;
    line-height:18px;
}
.ve-column-config-row {
    min-height:52px;
    border-bottom:1px solid #edf1f5;
    color:#1f2933;
    font-size:12px;
    cursor:move;
}
.ve-column-config-row:hover {
    background:#f5f9ff;
}
.ve-column-config-check-cell,
.ve-column-config-title-cell {
    display:flex;
    align-items:center;
    min-width:0;
    padding:0 12px;
    box-sizing:border-box;
}
.ve-column-config-check-cell {
    justify-content:center;
    cursor:default;
}
.ve-column-config-check-cell,
.ve-column-config-title-cell {
    border-right:1px solid #e5e9f0;
}
.ve-column-config-check-all {
    font-size:12px;
}
.ve-column-config-drag {
    width:22px;
    color:#a7b0bf;
    cursor:move;
    flex-shrink:0;
}
.ve-column-config-drag.is-locked {
    color:#d7dde6;
    cursor:move;
}
.ve-column-config-drag .drag-btn {
    display:flex;
    align-items:center;
    justify-content:center;
    width:16px;
    height:16px;
    line-height:16px;
    font-size:16px;
}
.ve-column-config-name {
    flex:1;
    overflow:hidden;
    text-overflow:ellipsis;
    white-space:nowrap;
}
.ve-column-config-footer {
    display:flex;
    justify-content:space-between;
    align-items:center;
}
.ve-column-config-footer-right {
    display:inline-flex;
    gap:12px;
}
</style>
